import { ca } from '@cian/analytics';

import { JSX, useEffect } from 'react';

import { ListingByPinCardsContainer } from 'shared/components/Cards';
import { SEOBlockByPin } from 'shared/components/SEOBlock';
import { ListingByPinContextProvider } from 'shared/context/ListingByPinContext';
import { useDisablePageScroll } from 'shared/hooks/useDisablePageScroll';
import { useGetCianAnalyticsPageViewObject } from 'shared/hooks/useGetCianAnalyticsPageViewObject';
import { useBrowserPageIsPageReloadingSelector } from 'shared/store/clientSide/slices/browserPage';
import { useGetOffersByActivePinQuery } from 'shared/store/serverSide/commercial-listing-map/desktop/v2/offers/get-offers-by-pin/query';
import { getIsBrowserBuild } from 'shared/utils/isBuildType';
import { memoWithDisplayName } from 'shared/utils/memoWithDisplayName';

import { BackToListingButtonContainer } from '../BackToListingButton';
import { ListingLoadErrorView } from '../ListingLoadErrorView';
import { ListingLoadingView } from '../ListingLoadingView';
import { ListingNoCards } from '../ListingNoCards';
import { ListingByPinPaginationContainer } from '../Pagination';
import { VisitedOffersAnalytics } from '../VisitedOffersAnalytics';

import { ListingLayout } from './ListingLayout';

export const ListingByPinContainer = memoWithDisplayName(() => {
  const { data, isFetching, refetch, status } = useGetOffersByActivePinQuery();

  const getCianAnalyticsPageViewObject = useGetCianAnalyticsPageViewObject();
  const isPageReloading = useBrowserPageIsPageReloadingSelector();

  const showSpinner = (isFetching || isPageReloading) && getIsBrowserBuild();

  useDisablePageScroll(showSpinner);

  useEffect(() => {
    if (data) {
      ca('pageviewSite', getCianAnalyticsPageViewObject({ offersDataByPin: data }));
    }
  }, [data]);

  switch (status) {
    case 'loading':
      return <ListingLoadingView />;
    case 'error':
      return (
        <ListingLayout
          banners={null}
          cards={null}
          isFetching={showSpinner}
          navigation={<BackToListingButtonContainer />}
          pagination={null}
          renderNoCards={(): JSX.Element => <ListingLoadErrorView isLoading={isFetching} onRetry={refetch} />}
          seo={null}
          similarCards={null}
        />
      );
    case 'success':
      return (
        <ListingByPinContextProvider value={data}>
          <ListingLayout
            banners={null}
            cards={<ListingByPinCardsContainer />}
            isFetching={showSpinner}
            navigation={<BackToListingButtonContainer />}
            pagination={<ListingByPinPaginationContainer />}
            renderNoCards={(): JSX.Element => <ListingNoCards />}
            seo={<SEOBlockByPin />}
            similarCards={null}
          />
          <VisitedOffersAnalytics isFetchingOffers={isFetching} offers={data.offers} />
        </ListingByPinContextProvider>
      );
  }
}, 'ListingByPinContainer');
